import { useEffect, useRef } from 'react';
import {
    useForm,
    type FieldValues,
    type UseFormProps,
    type UseFormReturn,
} from 'react-hook-form';
import { notifyUnsavedChanges, registerUnsavedChanges } from '@/shared/model/unsavedChanges';

/** Создаёт форму с защитой несохранённых изменений при уходе и перезагрузке */
export const useUnsavedForm = <
    TFieldValues extends FieldValues = FieldValues,
    TContext = unknown,
    TTransformedValues = TFieldValues,
>(
    props?: UseFormProps<TFieldValues, TContext, TTransformedValues>,
    enabled = true,
    group?: string,
): UseFormReturn<TFieldValues, TContext, TTransformedValues> => {
    const form = useForm<TFieldValues, TContext, TTransformedValues>(props);
    const { reset, subscribe } = form;
    const dirtyRef = useRef(form.formState.isDirty);
    dirtyRef.current = form.formState.isDirty;

    useEffect(() => {
        if (!enabled) return;

        const unregister = registerUnsavedChanges({
            group,
            when: () => dirtyRef.current,
            onDiscard: () => reset(),
        });

        const unsubscribe = subscribe({
            formState: { isDirty: true },
            callback: ({ isDirty }) => {
                dirtyRef.current = Boolean(isDirty);
                notifyUnsavedChanges();
            },
        });

        return () => {
            unsubscribe();
            unregister();
        };
    }, [enabled, group, reset, subscribe]);

    return form;
};
